<template>
  <div class="relative">
    <HintLabels
      :colors="colors"
      :labels="anomalyTypes"
      class="absolute bg-white bottom-0 left-4 z-50 p-2 rounded-md shadow-md"
    />
    <div id="map" ref="mapElement"></div>
  </div>
</template>
<script>
import config from '@/configs/configs.js'
import HintLabels from '../elements/HintLabels.vue'
import { movingMarkerSrc } from '@/mixins/movingMarker'

export default {
  props: ['result'],
  mixins: [movingMarkerSrc],
  data() {
    return {
      map: null,
      marker: null,
      anomalies: null,
      polyline: null,
      anomalyTypes: [
        this.$t('acceleration'),
        this.$t('braking'),
        this.$t('cornering'),
        this.$t('UNSUAL_SPEED'),
      ],
      colors: ['#0090fb', '#00e396', '#df44fa', '#ff4560'],
    }
  },
  methods: {
    loadMap() {
      var map = L.map(this.$refs['mapElement'], { tap: false }).setView(
        [
          this.result.route.locations[0].location[1],
          this.result.route.locations[0].location[0],
        ],
        12
      )
      L.tileLayer(config.tile, {
        attribution: '@ Carpo',
        id: 'mapbox/streets-v11',
        tileSize: 512,
        zoomOffset: -1,
        accessToken: config.leaflet,
      }).addTo(map)
      this.map = map

      this.polyline && this.map.removeLayer(this.polyline)
      this.anomalies && this.map.removeLayer(this.anomalies)

      this.pathGenerator()

      if (this.marker) {
        this.map.removeLayer(this.marker)
      }

      const fenceColor = this.result.fence.color.code
      if (
        this.result.fence.features.radius &&
        this.result.fence.features.coordinates
      ) {
        // Add circle
        const radius = this.result.fence.features.radius
        const coordinates = this.result.fence.features.coordinates

        this.marker = L.circle([coordinates[1], coordinates[0]], {
          radius,
          color: fenceColor,
          fillColor: fenceColor,
          interactive: false,
          fillOpacity: 0.5,
        }).addTo(this.map)
      } else {
        // Add polygon
        const coordinates = this.result.fence.features[0].map((coord) => [
          coord[1],
          coord[0],
        ])

        this.marker = L.polygon(coordinates, {
          color: fenceColor,
          fillColor: fenceColor,
          interactive: false,
          fillOpacity: 0.5,
        }).addTo(this.map)

        const bounds = this.marker.getBounds()
        this.map.fitBounds(bounds)
      }
    },
    pathGenerator() {
      const latlngs = this.result.route.locations.map(
        (location) => new L.LatLng(location.location[1], location.location[0])
      )

      // Prepare the latlngs1 array with colors
      const latlngs1 = this.result.route.locations.map((location) => ({
        latlng: new L.LatLng(location.location[1], location.location[0]),
        color:
          this.result.fence.max_speed &&
          location.speed > this.result.fence.max_speed
            ? this.colors[3]
            : 'black',
      }))

      // Remove existing polyline if present
      if (this.polyline) {
        this.map.removeLayer(this.polyline)
      }

      // Check if multiOptionsPolyline is defined
      if (!L.multiOptionsPolyline) {
        console.error(
          'L.multiOptionsPolyline is not defined. Ensure the plugin is correctly included.'
        )
        return
      }

      // Create the multiOptionsPolyline
      this.polyline = L.multiOptionsPolyline(
        latlngs1.map((point) => point.latlng),
        {
          multiOptions: {
            optionIdxFn: (latLng) => {
              // Find the index of the current latLng in latlngs1 array
              for (let i = 0; i < latlngs1.length; i++) {
                if (latLng.equals(latlngs1[i].latlng)) {
                  return i
                }
              }
              return 0 // Default option index
            },
            options: latlngs1.map((point) => ({ color: point.color })),
          },
          lineCap: 'butt',
          smoothFactor: 1,
        }
      ).addTo(this.map)

      this.map.fitBounds(L.latLngBounds(latlngs))
      this.map.addLayer(this.polyline)

      this.showSpeed(this.polyline)
      this.setMovingMarker(latlngs)
    },
    setMovingMarker(path) {
      var greenIcon = L.icon({
        iconUrl: '../../../../../pin.png',
        iconSize: [38, 38],
      })
      this.movingMarker = L.Marker.movingMarker(path, 30000, {
        autostart: false,
        icon: greenIcon,
      }).addTo(this.map)
      this.map.addLayer(this.movingMarker)

      let movingMarker = this.movingMarker
      movingMarker.once('click', function () {
        movingMarker.start()
        movingMarker.closePopup()
        movingMarker.unbindPopup()
        movingMarker.on('click', function () {
          if (movingMarker.isRunning()) {
            movingMarker.pause()
          } else {
            movingMarker.start()
          }
        })
      })
    },
    showSpeed(path) {
      path.on('click', (ev) => {
        var latlng = this.map.mouseEventToLatLng(ev.originalEvent)
        for (var i = 0; i < this.result.route.locations.length; i++) {
          if (
            this.distance(
              latlng.lat,
              latlng.lng,
              this.result.route.locations[i].location[1],
              this.result.route.locations[i].location[0],
              'K'
            ) <= 0.04
          ) {
            this.popupMaker(
              'SPEED',
              [
                this.result.route.locations[i].location[1],
                this.result.route.locations[i].location[0],
              ],
              `<span class="flex ltr"> ${this.result.route.locations[i].speed} Km/h</span>`,
              this.result.route.locations[i].datetime
            )
            break
          }
        }
      })
    },
    generatePulsatingMarker(radius, type) {
      const cssStyle = `
        width: ${radius}px;
        height: ${radius}px;
        background: ${this.colors[type - 1]};
        color: ${this.colors[type - 1]};
        box-shadow: 0 0 0 ${this.colors[type - 1]};
      `
      return L.divIcon({
        html: `<span style="${cssStyle}" class="pulse"></span>`,
        className: '',
      })
    },
    onEachFeature(feature, layer) {
      if (feature) {
        layer.bindPopup(
          `${this.popupDetail(
            this.$t('ANOMALY_DETECT') + ':',
            this.anomalyTypes[feature.properties.anomaly_type - 1]
          )}`
        )
      }
    },
    formatPersianDate(date) {
      const persianDate = new Date(date)
      persianDate.setMinutes(persianDate.getMinutes() + 210) // Adjust for Tehran timezone (3 hours and 30 minutes ahead of UTC)

      return persianDate.toLocaleString('fa-IR')
    },
    distance(lat1, lon1, lat2, lon2, unit) {
      var radlat1 = (Math.PI * lat1) / 180
      var radlat2 = (Math.PI * lat2) / 180
      var theta = lon1 - lon2
      var radtheta = (Math.PI * theta) / 180
      var dist =
        Math.sin(radlat1) * Math.sin(radlat2) +
        Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta)
      if (dist > 1) {
        dist = 1
      }
      dist = Math.acos(dist)
      dist = (dist * 180) / Math.PI
      dist = dist * 60 * 1.1515
      if (unit == 'K') {
        dist = dist * 1.609344
      }
      if (unit == 'N') {
        dist = dist * 0.8684
      }
      return dist
    },
    loadAnomaly(objs, this_) {
      const filteredObjs = objs.filter((obj) => obj.anomaly_type > 0)

      const featureCollection = {
        type: 'FeatureCollection',
        features: filteredObjs.map((obj) => ({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: obj.location,
          },
          properties: {
            datetime: obj.datetime,
            speed: obj.speed,
            anomaly_type: obj.anomaly_type,
          },
        })),
      }

      this.anomalies = L.geoJSON(featureCollection, {
        pointToLayer: function (feature, latlng) {
          return L.marker(
            [feature.geometry.coordinates[1], feature.geometry.coordinates[0]],
            {
              icon: this_.generatePulsatingMarker(
                15,
                feature.properties.anomaly_type
              ),
            }
          )
        },
        onEachFeature: this_.onEachFeature,
      }).addTo(this.map)
    },
    popupDetail(title, content) {
      return content != null && content != ''
        ? `<div class="py-1 flex justify-between items-center"> <span class="text-gray-500">${title} </span> <span class="capitalize font-bold">${content}</span> </div>`
        : ''
    },
    uniqBy(a, key) {
      return [...new Map(a.map((x) => [key(x), x])).values()]
    },
    popupMaker(content, latlong, content2 = '', date) {
      L.popup({ maxWidth: 200 })
        .setLatLng(latlong)
        .setContent(
          `
        <div class="py-1 flex flex-col  gap-2">
          <div class="flex flex-row items-center justify-between items-center gap-2 w-full">
              <span class="text-gray-500">${this.$i18n.t(content)}: </span>
              <div class="flex items-center space-x-1 flex-row-reverse	"><span class="capitalize font-bold">${content2}</span> </div>
            </div>
            <div class="flex flex-row items-center justify-between items-center gap-2 w-full">
              <span class="text-gray-500">${this.$i18n.t('TIME')}: </span>
              <span class="capitalize font-bold">
                ${this.formatPersianDate(date)} 
              </span>
            </div>
        </div>
        `
        )
        .addTo(this.map)
    },
  },
  mounted() {
    this.movingMarkerSrc().then((res) => {
      if (res) {
        this.loadMap()
        this.loadAnomaly(this.result.route.locations, this)
      }
    })
  },
  components: {
    HintLabels,
  },
}
</script>
